import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type CustomerAttribute as CustomerAttributeBase } from '~/gql/graphql'
import { type Unassigned, unassignedSymbol } from '~/utils'

type CustomerAttribute = App.PickGraphQL<CustomerAttributeBase, 'id' | 'name'>

export type CustomerAttributeSelectorProps<
  TCustomerAttribute extends CustomerAttribute,
> = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  customerAttributes: TCustomerAttribute[]
  isNullSelectable?: boolean
  nullText?: string
  onChange?: (customerAttributeId: TCustomerAttribute['id']) => void
  unsetText?: string
  value: TCustomerAttribute['id'] | Unassigned | null
}

export const CustomerAttributeSelector = React.forwardRef(
  <TCustomerAttribute extends CustomerAttribute>(
    {
      customerAttributes,
      isNullSelectable = false,
      nullText,
      onChange,
      unsetText,
      value: valueProp,
      ...rest
    }: CustomerAttributeSelectorProps<TCustomerAttribute>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const data = React.useMemo(
      () =>
        isNullSelectable
          ? [{ id: null, name: nullText }, ...customerAttributes]
          : customerAttributes,
      [customerAttributes, isNullSelectable, nullText],
    )
    const value = React.useMemo(
      () =>
        data.find(({ id }) => id === valueProp) ?? {
          id: unassignedSymbol,
          name: unsetText,
        },
      [data, unsetText, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (customerAttribute: TCustomerAttribute) => {
        onChange?.(customerAttribute.id)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={data}
        dataItemKey="id"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
